import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import CircaWordmark from '@/components/brand/CircaWordmark';
import CircaMark from '@/components/brand/CircaMark';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/scripts/AuthContext';
import {
  COMMUNITY_POINTS,
  FEATURES_COMING,
  FEATURES_NOW,
  PLAN_MATRIX_ROWS,
  PLAN_PRICE_DISCLAIMER,
  PLANS,
  SOCIAL_STATUS_NOTE,
  USE_CASES,
} from '@/lib/planMarketing';
import ClosedBetaInterestChrome from '@/components/ClosedBetaInterestBanner';

const AboutPage = () => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    document.title = 'Circa — Stay close to what matters';
    return () => {
      document.title = 'Circa — Stay close to what matters';
    };
  }, []);

  return (
    <ClosedBetaInterestChrome>
      {({ openDialog }) => (
    <div className="min-h-screen w-full circa-orbit-bg text-foreground">
      <header className="sticky top-0 z-20 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-6">
          <Link to="/" aria-label="Circa home">
            <CircaWordmark markClassName="h-7 w-7" />
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            {isAuthenticated ? (
              <Button size="sm" className="circa-glow" asChild>
                <Link to="/dashboard">Open Circa</Link>
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button size="sm" className="circa-glow" asChild>
                  <Link to="/signup">Sign up</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      <main>
        {/* Hero — brand, headline, support, CTAs on orbit gradient */}
        <section className="relative min-h-[70vh] overflow-hidden border-b border-border/40">
          <div aria-hidden className="absolute inset-0 circa-about-hero-glow" />
          <div className="relative mx-auto flex min-h-[70vh] max-w-5xl flex-col justify-end px-6 pb-16 pt-20 sm:pb-24 sm:pt-28">
            <p className="circa-about-fade-up text-sm font-medium tracking-wide text-primary">
              Circa
            </p>
            <h1 className="circa-about-fade-up circa-about-delay-1 mt-4 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl md:text-6xl">
              Stay close to what matters.
            </h1>
            <p className="circa-about-fade-up circa-about-delay-2 mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">
              For everyone who needs to stay aligned and in the loop—from small
              teams and schools to nonprofits and large organizations.
            </p>
            <div className="circa-about-fade-up circa-about-delay-3 mt-8 flex flex-wrap gap-3">
              {isAuthenticated ? (
                <Button size="lg" className="circa-glow" asChild>
                  <Link to="/dashboard">Open Circa</Link>
                </Button>
              ) : (
                <>
                  <Button size="lg" className="circa-glow" asChild>
                    <Link to="/signup">Get started</Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/login">Sign in</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            What Circa is about
          </h2>
          <div className="mt-6 max-w-2xl space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Circa is for everyone who needs alignment and to stay connected—
              small teams and clubs, schools and nonprofits, growing businesses,
              and large enterprises. Wherever people work together, Circa helps
              you stay in the loop without living in every conversation.
            </p>
            <p>
              Your workplace is private and invite-only: a home for updates,
              teams, and decisions that stay inside. Circa also looks
              outward—to people and communities beyond a single workplace—so
              you can stay close to the circle around you without opening the
              doors to your private work.
            </p>
            <p>
              The name comes from Latin <em>circa</em>, meaning &quot;around&quot;—built
              around the people and work you care about.
            </p>
          </div>
        </section>

        <section className="border-y border-border/40 bg-background/40">
          <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Messages that stay with the work
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Channels, DMs, and partner spaces live inside your workplace—not
              scattered across another app.
            </p>
            <figure className="mt-10">
              <div className="aspect-video w-full overflow-hidden rounded-lg border border-border/50 shadow-sm">
                <picture>
                  <source
                    srcSet={`${process.env.PUBLIC_URL}/marketing/shot-messages.webp`}
                    type="image/webp"
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/marketing/shot-messages.png`}
                    alt="Circa Messages showing a workplace channel conversation"
                    width={1600}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                  />
                </picture>
              </div>
              <figcaption className="mt-3 text-sm text-muted-foreground">
                Multi-person channels for standups, owners, and decisions—next
                to the people doing the work.
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Teams with a shared feed
          </h2>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Each team has a home for updates, channels, and board work so
            context does not get lost.
          </p>
          <figure className="mt-10">
            <div className="aspect-video w-full overflow-hidden rounded-lg border border-border/50 shadow-sm">
              <picture>
                <source
                  srcSet={`${process.env.PUBLIC_URL}/marketing/shot-team.webp`}
                  type="image/webp"
                />
                <img
                  src={`${process.env.PUBLIC_URL}/marketing/shot-team.png`}
                  alt="Circa team feed with a sprint kickoff update"
                  width={1600}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-top"
                />
              </picture>
            </div>
            <figcaption className="mt-3 text-sm text-muted-foreground">
              Team feed for updates, with the channel right beside it—so
              context stays with the work.
            </figcaption>
          </figure>
        </section>

        <section className="border-y border-border/40 bg-background/40">
          <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Your workplace
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Everyday ways Circa helps groups of every size stay aligned and in
              the loop.
            </p>
            <figure className="mt-10">
              <div className="aspect-video w-full overflow-hidden rounded-lg border border-border/50 shadow-sm">
                <picture>
                  <source
                    srcSet={`${process.env.PUBLIC_URL}/marketing/hero-workplace.webp`}
                    type="image/webp"
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/marketing/hero-workplace.png`}
                    alt="Circa workplace home with team activity and updates"
                    width={1600}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover object-top"
                  />
                </picture>
              </div>
              <figcaption className="mt-3 text-sm text-muted-foreground">
                Your private workplace home—updates and activity in one place.
              </figcaption>
            </figure>
            <ul className="mt-12 space-y-10">
              {USE_CASES.map((item, i) => (
                <li
                  key={item.title}
                  className="flex gap-4 sm:gap-6"
                  style={{ animationDelay: `${0.05 * i}s` }}
                >
                  <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <CircaMark className="h-4 w-4" decorative />
                  </span>
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      {item.title}
                    </h3>
                    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {item.body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Communities and Social
          </h2>
          <p className="mt-2 max-w-2xl text-base leading-relaxed text-muted-foreground">
            Circa has two complementary sides. Your workplace stays private.
            Social and communities are how you stay connected to the wider
            circle—without making internal work public by default.
          </p>
          <figure className="mt-10">
            <div className="aspect-video w-full overflow-hidden rounded-lg border border-border/50 shadow-sm">
              <picture>
                <source
                  srcSet={`${process.env.PUBLIC_URL}/marketing/shot-social.webp`}
                  type="image/webp"
                />
                <img
                  src={`${process.env.PUBLIC_URL}/marketing/shot-social.png`}
                  alt="Circa Social Explore with communities to discover"
                  width={1600}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-top"
                />
              </picture>
            </div>
            <figcaption className="mt-3 text-sm text-muted-foreground">
              Explore communities and people on Circa Social—beyond any single
              workplace.
            </figcaption>
          </figure>
          <figure className="mt-12">
            <div className="aspect-video w-full overflow-hidden rounded-lg border border-border/50 shadow-sm">
              <picture>
                <source
                  srcSet={`${process.env.PUBLIC_URL}/marketing/shot-community.webp`}
                  type="image/webp"
                />
                <img
                  src={`${process.env.PUBLIC_URL}/marketing/shot-community.png`}
                  alt="Neighborhood Makers community feed on Circa Social"
                  width={1600}
                  height={900}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-top"
                />
              </picture>
            </div>
            <figcaption className="mt-3 text-sm text-muted-foreground">
              Communities have their own feed, guidelines, and members—shared
              interests without opening your private org.
            </figcaption>
          </figure>
          <ul className="mt-12 space-y-10">
            {COMMUNITY_POINTS.map((item) => (
              <li key={item.title} className="flex gap-4 sm:gap-6">
                <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                  <CircaMark className="h-4 w-4" decorative />
                </span>
                <div>
                  <h3 className="text-lg font-medium text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-10 max-w-2xl text-sm text-muted-foreground">
            {SOCIAL_STATUS_NOTE}
          </p>
        </section>

        <section className="border-y border-border/40 bg-background/40">
          <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Features
            </h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              What you can use today, and what we are still building.
            </p>
            <div className="mt-10 grid gap-12 md:grid-cols-2">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
                  Available now
                </h3>
                <ul className="mt-4 space-y-3">
                  {FEATURES_NOW.map((f) => (
                    <li
                      key={f}
                      className="flex gap-2 text-sm leading-relaxed text-foreground/90"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Coming soon
                </h3>
                <ul className="mt-4 space-y-3">
                  {FEATURES_COMING.map((f) => (
                    <li
                      key={f}
                      className="flex gap-2 text-sm leading-relaxed text-muted-foreground"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="plans" className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            Plans for your workplace
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Simple plans by size of your group—members, storage, and catch-up
            summaries. The details below are what Circa applies today.
          </p>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PLANS.map((plan) => (
              <div key={plan.id} className="border-t-2 border-primary/40 pt-4">
                <p className="text-sm font-medium text-muted-foreground">
                  {plan.name}
                </p>
                <p className="mt-1 text-2xl font-semibold tracking-tight">
                  {plan.priceLabel}
                </p>
                <p className="text-xs text-muted-foreground">{plan.priceNote}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 space-y-4 sm:hidden">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className="rounded-lg border border-border/60 p-4 space-y-3"
              >
                <div>
                  <p className="text-base font-semibold text-foreground">
                    {plan.name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {plan.priceLabel}{" "}
                    <span className="text-xs">{plan.priceNote}</span>
                  </p>
                </div>
                <dl className="space-y-2 text-sm">
                  {PLAN_MATRIX_ROWS.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-baseline justify-between gap-3 border-t border-border/40 pt-2"
                    >
                      <dt className="text-muted-foreground min-w-0">
                        {row.label}
                        {row.comingSoon ? (
                          <span className="block text-xs">Not available yet</span>
                        ) : null}
                      </dt>
                      <dd className="font-medium text-foreground text-right shrink-0">
                        {row.cells[plan.id]}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>

          <div className="mt-12 hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[40rem] text-left text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 pr-4 font-medium text-muted-foreground">
                    Included
                  </th>
                  {PLANS.map((p) => (
                    <th
                      key={p.id}
                      className="px-2 py-3 font-medium text-foreground"
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLAN_MATRIX_ROWS.map((row) => (
                  <tr
                    key={row.label}
                    className="border-b border-border/60 align-top"
                  >
                    <th
                      scope="row"
                      className="py-3 pr-4 font-normal text-foreground"
                    >
                      {row.label}
                      {row.comingSoon ? (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Not available yet
                        </span>
                      ) : null}
                    </th>
                    {PLANS.map((p) => (
                      <td
                        key={p.id}
                        className="px-2 py-3 text-muted-foreground"
                      >
                        {row.cells[p.id]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            {PLAN_PRICE_DISCLAIMER}
          </p>
        </section>
      </main>

      <footer className="border-t border-border/60">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-10 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Workplaces on Circa are invite-only.{" "}
            <button
              type="button"
              className="font-medium text-primary underline-offset-4 hover:underline"
              onClick={openDialog}
            >
              Register interest
            </button>
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="text-primary underline-offset-4 hover:underline"
              >
                Open Circa
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </footer>
    </div>
      )}
    </ClosedBetaInterestChrome>
  );
};

export default AboutPage;
